import React from 'react';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import BuildRoundedIcon from '@mui/icons-material/BuildRounded';
import GridOnRoundedIcon from '@mui/icons-material/GridOnRounded';
import RemoveCircleRoundedIcon from '@mui/icons-material/RemoveCircleRounded';
import ShoppingBagRoundedIcon from '@mui/icons-material/ShoppingBagRounded';
import SportsEsportsRoundedIcon from '@mui/icons-material/SportsEsportsRounded';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import LetterTile from './LetterTile';
import { LETTER_POINTS } from './config/gameConfig';
import { getRarity } from './config/rarityConfig';
import { SHOP_KINDS, SHOP_KIND_CHIP_LABELS } from './config/shopKinds';
import { normalizeTileModifiers } from './config/shopOptions';
import { useScrabbleGameContext } from './context/ScrabbleGameContext';

const SHOP_KIND_ICONS = {
  [SHOP_KINDS.BAG_TILE_UPGRADE]: BuildRoundedIcon,
  [SHOP_KINDS.NEW_RANDOM_TILE]: AddCircleRoundedIcon,
  [SHOP_KINDS.REMOVE_BAG_TILE]: RemoveCircleRoundedIcon,
  [SHOP_KINDS.GAME_MODIFIER_GRANT]: AutoAwesomeRoundedIcon,
  [SHOP_KINDS.BOARD_MODIFIER_GRANT]: GridOnRoundedIcon,
};

function ShopOptionCard({ option, isSelected, onSelect, onInspectTile }) {
  const isGameModifierOption = option.kind === SHOP_KINDS.GAME_MODIFIER_GRANT;
  const isBoardModifierOption = option.kind === SHOP_KINDS.BOARD_MODIFIER_GRANT;
  const rarity = getRarity(option.rarity);
  const previewTile = {
    id: `shop-preview-${option.id}`,
    letter: option.tileLetter || '?',
    modifiers: normalizeTileModifiers(option.tileModifiers),
    points: LETTER_POINTS[option.tileLetter] || 0,
  };

  const kindLabel = SHOP_KIND_CHIP_LABELS[option.kind] || 'Tile Upgrade';
  const KindIcon = SHOP_KIND_ICONS[option.kind];
  const optionName =
    option.kind === SHOP_KINDS.NEW_RANDOM_TILE
      ? `Add ${option.tileLetter} to your bag`
      : option.kind === SHOP_KINDS.REMOVE_BAG_TILE
        ? `Remove ${option.tileLetter} from your bag`
        : option.kind === SHOP_KINDS.GAME_MODIFIER_GRANT
          ? option.modifierLabel || 'Game Modifier'
          : option.kind === SHOP_KINDS.BOARD_MODIFIER_GRANT
            ? option.modifierLabel || 'Board Modifier'
            : option.modifierLabel || 'Tile Upgrade';
  const tileModifiersObj = normalizeTileModifiers(option.tileModifiers);
  const newTileHasModifier =
    tileModifiersObj.score > 0 ||
    tileModifiersObj.multiplier > 1 ||
    tileModifiersObj.extras.length > 0;
  const optionDescription =
    option.kind === SHOP_KINDS.NEW_RANDOM_TILE
      ? newTileHasModifier
        ? option.description || ''
        : ''
      : option.kind === SHOP_KINDS.REMOVE_BAG_TILE
        ? 'Permanently removes this tile from your bag.'
        : option.kind === SHOP_KINDS.GAME_MODIFIER_GRANT
          ? option.modifierDescription || option.description || 'Applies a persistent game effect.'
          : option.kind === SHOP_KINDS.BOARD_MODIFIER_GRANT
            ? option.modifierDescription || option.description || 'Place this upgrade on any empty board square.'
            : option.modifierDescription || 'Applies this modifier to the selected tile.';
  const isLegendary = rarity.id === 'legendary';
  const isEpic = rarity.id === 'epic';
  const isRare = rarity.id === 'rare';
  const edgeOpacity = isLegendary ? (isSelected ? 0.58 : 0.45) : isEpic ? (isSelected ? 0.46 : 0.34) : isRare ? (isSelected ? 0.4 : 0.28) : isSelected ? 0.32 : 0.22;
  const cardGlowOpacity = isLegendary ? (isSelected ? 0.44 : 0.28) : isEpic ? (isSelected ? 0.34 : 0.22) : isRare ? (isSelected ? 0.26 : 0.16) : isSelected ? 0.18 : 0.1;
  const hasShine = isLegendary || isEpic;

  return (
    <Button
      fullWidth
      variant={isSelected ? 'contained' : 'outlined'}
      onClick={() => onSelect(option.id)}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        justifyContent: 'flex-start',
        textAlign: 'left',
        textTransform: 'none',
        borderRadius: 2,
        padding: 1.1,
        borderColor: alpha(rarity.color, isSelected ? 0.62 : 0.4),
        boxShadow: `0 0 0 1px ${alpha(rarity.color, cardGlowOpacity)}, 0 8px 20px ${alpha(rarity.color, cardGlowOpacity * 0.72)}`,
        transition: 'transform 150ms ease, box-shadow 220ms ease, border-color 220ms ease',
        '&:hover': {
          transform: 'translateY(-1px)',
          borderColor: alpha(rarity.color, isSelected ? 0.76 : 0.55),
          boxShadow: `0 0 0 1px ${alpha(rarity.color, cardGlowOpacity + 0.08)}, 0 12px 24px ${alpha(rarity.color, cardGlowOpacity * 0.92)}`,
        },
        '&::before': hasShine
          ? {
              content: '""',
              position: 'absolute',
              top: -2,
              bottom: -2,
              right: '-18%',
              width: isLegendary ? '22%' : '18%',
              transform: 'skewX(-17deg)',
              pointerEvents: 'none',
              background: `linear-gradient(110deg, ${alpha(rarity.color, 0)} 0%, ${alpha(
                '#ffffff',
                isLegendary ? 0.64 : 0.44
              )} 48%, ${alpha(rarity.color, 0)} 100%)`,
              animation: isLegendary
                ? 'shopRarityLegendaryShine 2.4s ease-in-out infinite'
                : 'shopRarityEpicShine 3.1s ease-in-out infinite',
            }
          : undefined,
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: '32%',
          pointerEvents: 'none',
          background: `linear-gradient(90deg, ${alpha(rarity.color, 0)} 0%, ${alpha(
            rarity.color,
            edgeOpacity
          )} 100%)`,
        },
        '@keyframes shopRarityLegendaryShine': {
          '0%': {
            right: '-26%',
            opacity: 0,
          },
          '10%': {
            opacity: 1,
          },
          '32%': {
            right: '108%',
            opacity: 0,
          },
          '100%': {
            right: '108%',
            opacity: 0,
          },
        },
        '@keyframes shopRarityEpicShine': {
          '0%': {
            right: '-24%',
            opacity: 0,
          },
          '12%': {
            opacity: 0.9,
          },
          '28%': {
            right: '96%',
            opacity: 0,
          },
          '100%': {
            right: '96%',
            opacity: 0,
          },
        },
      }}
    >
      <Stack spacing={0.8} sx={{ width: '100%', alignItems: 'flex-start' }}>
        <Stack direction="row" spacing={0.6} alignItems="center" useFlexGap flexWrap="wrap">
          <Chip
            size="medium"
            label={
              <Stack direction="row" spacing={0.4} alignItems="center">
                <span>{kindLabel}</span>
                {KindIcon && <KindIcon sx={{ fontSize: 13 }} />}
              </Stack>
            }
            sx={{
              height: 26,
              borderRadius: 1.2,
              fontWeight: 800,
              fontSize: 11.5,
            }}
          />

          <Chip
            size="small"
            label={rarity.label}
            sx={{
              height: 24,
              borderRadius: 1,
              fontWeight: 800,
              fontSize: 11,
              color: rarity.color,
              border: `1px solid ${alpha(rarity.color, 0.55)}`,
              backgroundColor: alpha(rarity.color, 0.12),
              boxShadow: `0 0 0 1px ${rarity.glow}`,
              textShadow: isLegendary ? `0 0 10px ${alpha(rarity.color, 0.42)}` : 'none',
            }}
          />
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
          {isGameModifierOption ? (
            <Box
              sx={{
                width: 112,
                height: 112,
                flexShrink: 0,
                borderRadius: 2,
                border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.28)}`,
                background: (theme) =>
                  `linear-gradient(140deg, ${alpha(theme.palette.primary.main, 0.16)} 0%, ${alpha(
                    theme.palette.background.paper,
                    0.96
                  )} 88%)`,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <Stack spacing={0.7} alignItems="center">
                <BoltRoundedIcon color="primary" sx={{ fontSize: 34 }} />
                <Typography sx={{ fontSize: 11, fontWeight: 800, opacity: 0.86 }}>PERSISTENT</Typography>
              </Stack>
            </Box>
          ) : isBoardModifierOption ? (
            <Box
              sx={{
                width: 112,
                height: 112,
                flexShrink: 0,
                borderRadius: 2,
                border: (theme) => `1px solid ${alpha(theme.palette.success.main, 0.28)}`,
                background: (theme) =>
                  `linear-gradient(140deg, ${alpha(theme.palette.success.main, 0.16)} 0%, ${alpha(
                    theme.palette.background.paper,
                    0.96
                  )} 88%)`,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <Stack spacing={0.7} alignItems="center">
                <GridOnRoundedIcon color="success" sx={{ fontSize: 34 }} />
                <Typography sx={{ fontSize: 11, fontWeight: 800, opacity: 0.86 }}>BOARD</Typography>
              </Stack>
            </Box>
          ) : (
            <Box
              sx={{ width: 112, height: 112, flexShrink: 0 }}
              onMouseEnter={() => onInspectTile?.(previewTile)}
              onMouseLeave={() => onInspectTile?.(null)}
            >
              <LetterTile tile={previewTile} draggable={false} compact={false} variant="rack" />
            </Box>
          )}

          <Stack spacing={0.4} sx={{ alignItems: 'flex-start' }}>
            <Typography sx={{ fontWeight: 800, fontSize: 14 }}>{optionName}</Typography>
            {optionDescription ? (
              <Typography sx={{ fontSize: 11.5, opacity: 0.85 }}>{optionDescription}</Typography>
            ) : null}
          </Stack>
        </Stack>
      </Stack>
    </Button>
  );
}

const progressionButtonSx = {
  color: '#ffffff',
  backgroundColor: 'accent.main',
  borderColor: 'accent.main',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    backgroundColor: 'accent.dark',
    borderColor: 'accent.dark',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: -2,
    left: -130,
    width: '34%',
    height: 'calc(100% + 4px)',
    transform: 'skewX(-22deg)',
    background:
      'linear-gradient(110deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.1) 34%, rgba(255,255,255,0.72) 50%, rgba(255,255,255,0.08) 66%, rgba(255,255,255,0) 100%)',
    pointerEvents: 'none',
    animation: 'progressionButtonGlimmer 3.7s ease-in-out infinite',
  },
  '@keyframes progressionButtonGlimmer': {
    '0%': {
      left: '-130%',
      opacity: 0,
    },
    '8%': {
      opacity: 1,
    },
    '24%': {
      left: '142%',
      opacity: 0,
    },
    '100%': {
      left: '142%',
      opacity: 0,
    },
  },
  '&.Mui-disabled': {
    backgroundColor: (theme) => alpha(theme.palette.accent.main, 0.45),
    color: (theme) => alpha(theme.palette.common.white, 0.82),
  },
  '&.Mui-disabled::after': {
    display: 'none',
  },
};

export function ShopActionButtons({ isInventoryOpen, onToggleInventory }) {
  const {
    roundIndex,
    shopPicksRemaining = 1,
    shopRerollsRemaining = 0,
    shopOptions = [],
    selectedShopOptionId,
    bagTiles,
    heldBoardModifiers = [],
    confirmShopSelection: onConfirmShopSelection,
    skipShopSelection: onSkipShopSelection,
    rerollShopOptions: onRerollShopOptions,
  } = useScrabbleGameContext();

  const bagTileCount = bagTiles?.length ?? 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.7 }}>
      <Stack direction="row" spacing={0.7}>
        <Button
          fullWidth
          variant="contained"
          disabled={!selectedShopOptionId}
          onClick={onConfirmShopSelection}
          sx={progressionButtonSx}
        >
          {shopPicksRemaining > 1 ? (
            <Stack direction="row" spacing={0.8} alignItems="center">
              <span>Continue</span>
              <Chip
                size="small"
                label={shopPicksRemaining - 1}
                sx={{
                  height: 19,
                  minWidth: 22,
                  borderRadius: 0.9,
                  fontSize: 10.5,
                  fontWeight: 800,
                  color: 'accent.dark',
                  backgroundColor: (theme) => alpha(theme.palette.common.white, 0.88),
                }}
              />
            </Stack>
          ) : `Round ${roundIndex + 1}`}
        </Button>
        <Button fullWidth variant="outlined" onClick={onSkipShopSelection}>
          Skip
        </Button>
      </Stack>
      <Stack direction="row" spacing={0.6}>
        <Button
          variant="outlined"
          sx={{ flex: 1, fontSize: 12 }}
          disabled={shopRerollsRemaining <= 0 || !shopOptions.length}
          onClick={onRerollShopOptions}
        >
          <Stack direction="row" spacing={0.5} alignItems="center">
            <span>Reroll</span>
            <Chip
              size="small"
              label={shopRerollsRemaining}
              sx={{
                height: 17,
                minWidth: 22,
                borderRadius: 0.9,
                fontSize: 10,
                fontWeight: 800,
                color: 'text.primary',
                backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.08),
                '& .MuiChip-label': { px: 0.5 },
              }}
            />
          </Stack>
        </Button>
        <Button
          variant={isInventoryOpen ? 'contained' : 'outlined'}
          color="secondary"
          sx={{ flex: 1, fontSize: 12 }}
          onClick={onToggleInventory}
        >
          <Stack direction="row" spacing={0.5} alignItems="center">
            <span>{isInventoryOpen ? 'Close' : 'Inventory'}</span>
            {heldBoardModifiers.length > 0 ? (
              <Chip
                size="small"
                label={heldBoardModifiers.length}
                sx={{
                  height: 17,
                  minWidth: 22,
                  borderRadius: 0.9,
                  fontSize: 10,
                  fontWeight: 800,
                  color: 'warning.dark',
                  backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.22),
                  '& .MuiChip-label': { px: 0.5 },
                }}
              />
            ) : (
              <Chip
                size="small"
                label={bagTileCount}
                sx={{
                  height: 17,
                  minWidth: 22,
                  borderRadius: 0.9,
                  fontSize: 10,
                  fontWeight: 800,
                  color: 'secondary.main',
                  backgroundColor: (theme) => alpha(theme.palette.common.white, 0.9),
                  '& .MuiChip-label': { px: 0.5 },
                }}
              />
            )}
          </Stack>
        </Button>
      </Stack>
    </Box>
  );
}

function ShopOverlay({ onInspectTile }) {
  const {
    roundIndex,
    shopPicksRemaining = 1,
    shopRerollsRemaining = 0,
    shopOptions = [],
    selectedShopOptionId,
    selectShopOption: onSelectShopOption,
    confirmShopSelection: onConfirmShopSelection,
    skipShopSelection: onSkipShopSelection,
    rerollShopOptions: onRerollShopOptions,
  } = useScrabbleGameContext();

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 980,
        borderRadius: 3,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        backgroundColor: 'background.paper',
        padding: { xs: 1.2, md: 1.5 },
        boxShadow: (theme) => `0 10px 25px ${alpha(theme.palette.common.black, 0.18)}`,
      }}
    >
      <Stack spacing={1.15}>
        <Box
          sx={{
            borderRadius: 2,
            border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
            background: (theme) =>
              `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(
                theme.palette.background.paper,
                0.96
              )} 72%)`,
            px: 1.2,
            py: 0.75,
          }}
        >
          <Stack spacing={0.5}>
            <Typography sx={{ fontWeight: 800, fontSize: 13.5 }}>
              Shop Rewards
            </Typography>
            <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip
                icon={<SportsEsportsRoundedIcon sx={{ fontSize: 14 }} />}
                label={`Round ${roundIndex} Complete`}
                size="small"
                sx={{ fontWeight: 700 }}
                color="primary"
                variant="outlined"
              />
              <Chip
                icon={<ShoppingBagRoundedIcon sx={{ fontSize: 14 }} />}
                label={`${shopPicksRemaining} Pick${shopPicksRemaining === 1 ? '' : 's'} Available`}
                size="small"
                sx={{ fontWeight: 700 }}
                color="secondary"
                variant="outlined"
              />
            </Stack>
          </Stack>
        </Box>

        <Stack spacing={0.85}>
          {shopOptions.map((option) => (
            <ShopOptionCard
              key={option.id}
              option={option}
              isSelected={selectedShopOptionId === option.id}
              onSelect={onSelectShopOption}
              onInspectTile={onInspectTile}
            />
          ))}
        </Stack>

        <Stack direction="row" spacing={1} sx={{ marginTop: 0.5, display: { xs: 'none', lg: 'flex' } }}>
          <Button
            variant="contained"
            disabled={!selectedShopOptionId}
            onClick={onConfirmShopSelection}
            sx={progressionButtonSx}
          >
            {shopPicksRemaining > 1 ? (
              <Stack direction="row" spacing={0.8} alignItems="center">
                <span>Continue</span>
                <Chip
                  size="small"
                  label={shopPicksRemaining - 1}
                  sx={{
                    height: 19,
                    minWidth: 22,
                    borderRadius: 0.9,
                    fontSize: 10.5,
                    fontWeight: 800,
                    color: 'accent.dark',
                    backgroundColor: (theme) => alpha(theme.palette.common.white, 0.88),
                  }}
                />
              </Stack>
            ) : `Round ${roundIndex + 1}`}
          </Button>
          <Button variant="outlined" onClick={onSkipShopSelection}>
            Skip
          </Button>
          <Button
            variant="outlined"
            onClick={onRerollShopOptions}
            disabled={shopRerollsRemaining <= 0 || !shopOptions.length}
          >
            <Stack direction="row" spacing={0.7} alignItems="center">
              <span>Reroll</span>
              <Chip
                size="small"
                label={shopRerollsRemaining}
                sx={{
                  height: 18,
                  minWidth: 20,
                  borderRadius: 0.9,
                  fontSize: 10,
                  fontWeight: 800,
                  color: 'text.primary',
                  backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.08),
                }}
              />
            </Stack>
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

export default ShopOverlay;